// src/whatsappChat/components/MessageBubble.jsx
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCheckDouble,
  faCircleExclamation,
  faFile,
  faArrowUpRightFromSquare,
  faListUl,
} from '@fortawesome/free-solid-svg-icons';
import { describeError, describeMessage, formatTime } from '../api/content';

function StatusTicks({ status }) {
  if (status === 'failed') {
    return (
      <span className="wa-status-failed">
        <FontAwesomeIcon icon={faCircleExclamation} /> Not delivered
      </span>
    );
  }
  if (status === 'read') {
    return <FontAwesomeIcon icon={faCheckDouble} className="wa-tick is-read" title="Read" />;
  }
  if (status === 'delivered') {
    return <FontAwesomeIcon icon={faCheckDouble} className="wa-tick" title="Delivered" />;
  }
  // Accepted by WhatsApp only; never shown as delivered, however long it stays here.
  if (status === 'sent') {
    return <FontAwesomeIcon icon={faCheck} className="wa-tick" title="Sent, not yet delivered" />;
  }
  return null;
}

function Media({ view }) {
  const { mediaType, link, filename, label } = view;

  if (link && (mediaType === 'image' || mediaType === 'sticker')) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className="wa-media-image">
        <img src={link} alt={filename || mediaType} loading="lazy" />
      </a>
    );
  }
  if (link && mediaType === 'video') {
    return <video className="wa-media-video" src={link} controls preload="metadata" />;
  }
  if (link && mediaType === 'audio') {
    return <audio className="wa-media-audio" src={link} controls preload="none" />;
  }

  const inner = (
    <>
      <FontAwesomeIcon icon={faFile} />
      <span>{filename || label}</span>
    </>
  );
  return link ? (
    <a href={link} target="_blank" rel="noopener noreferrer" className="wa-media-file">
      {inner}
    </a>
  ) : (
    <span className="wa-media-file">{inner}</span>
  );
}

function Body({ view }) {
  switch (view.kind) {
    case 'text':
      return (
        <>
          {view.label && <span className="wa-msg-label">{view.label}</span>}
          <div className="wa-msg-text">{view.text}</div>
        </>
      );

    case 'media':
      return (
        <>
          <Media view={view} />
          {view.text && <div className="wa-msg-text">{view.text}</div>}
        </>
      );

    case 'template':
      return (
        <>
          {!view.resolved && <span className="wa-msg-label">Template · {view.templateName}</span>}
          {view.headerMedia && <span className="wa-msg-label">{view.headerMedia}</span>}
          {view.header && <div className="wa-msg-header">{view.header}</div>}
          {view.text && <div className="wa-msg-text">{view.text}</div>}
          {view.footer && <div className="wa-msg-footer">{view.footer}</div>}
          {view.buttons.length > 0 && (
            <div className="wa-msg-buttons">
              {view.buttons.map((text, i) => (
                <span key={`${text}-${i}`} className="wa-msg-button">{text}</span>
              ))}
            </div>
          )}
        </>
      );

    case 'interactive':
      return (
        <>
          {view.header && <div className="wa-msg-header">{view.header}</div>}
          {view.text && <div className="wa-msg-text">{view.text}</div>}
          {view.footer && <div className="wa-msg-footer">{view.footer}</div>}
          {view.sections.some((s) => s.rows.length > 0) && (
            <div className="wa-msg-sections">
              {view.sections.map((section, i) => (
                <div key={i} className="wa-msg-section">
                  {section.title && <div className="wa-msg-section-title">{section.title}</div>}
                  {section.rows.map((row) => (
                    <div key={row.id || row.title} className="wa-msg-section-row">
                      <span>{row.title}</span>
                      {row.description && <small>{row.description}</small>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {(view.buttons.length > 0 || view.listButton || view.cta) && (
            <div className="wa-msg-buttons">
              {view.buttons.map((text, i) => (
                <span key={`${text}-${i}`} className="wa-msg-button">{text}</span>
              ))}
              {view.listButton && (
                <span className="wa-msg-button">
                  <FontAwesomeIcon icon={faListUl} /> {view.listButton}
                </span>
              )}
              {view.cta &&
                (view.cta.url ? (
                  <a className="wa-msg-button" href={view.cta.url} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> {view.cta.text}
                  </a>
                ) : (
                  <span className="wa-msg-button">{view.cta.text}</span>
                ))}
            </div>
          )}
        </>
      );

    default:
      return <span className="wa-msg-label">{view.label}</span>;
  }
}

function MessageBubbleBase({ message }) {
  const outbound = message.direction === 'outbound';
  const failed = outbound && message.status === 'failed';
  const view = describeMessage(message);
  const error = failed ? describeError(message) : null;

  return (
    <div className={`wa-msg ${outbound ? 'is-out' : 'is-in'}`}>
      <div className={`wa-bubble${failed ? ' is-failed' : ''}`}>
        <Body view={view} />
        <div className="wa-msg-meta">
          <span>{formatTime(message.message_at)}</span>
          {outbound && <StatusTicks status={message.status} />}
        </div>
      </div>
      {error && <div className="wa-msg-error">{error}</div>}
    </div>
  );
}

export const MessageBubble = React.memo(MessageBubbleBase);
